<template>
  <article
    ref="revealTarget"
    class="article-list-item"
    :class="{ 'is-reveal-ready': isRevealReady, 'is-revealed': isRevealed }"
  >
    <nuxt-link class="article-media-link" :to="articlePath">
      <site-media :source="article.cover_url" :alt="article.title" />
      <span v-if="isVideo" class="video-play-mark" aria-hidden="true">
        <i class="i-lucide-play"></i>
      </span>
      <span v-if="isVideo && videoDuration" class="video-duration">
        {{ videoDuration }}
      </span>
    </nuxt-link>

    <div class="article-list-copy">
      <div class="article-list-text">
        <div class="article-list-meta">
          <span v-if="article.source" class="article-list-meta-item">
            文章来源：{{ article.source }}
          </span>
          <span v-if="article.author" class="article-list-meta-item">
            作者：{{ article.author }}
          </span>
          <span class="article-list-meta-item">
            发布时间：
            <time :datetime="article.publish_date">{{ publishDate }}</time>
          </span>
        </div>

        <h2>
          <nuxt-link :to="articlePath">{{ article.title }}</nuxt-link>
        </h2>

        <p v-if="article.summary">
          {{ article.summary }}
        </p>
      </div>

      <nuxt-link class="article-list-action" :to="articlePath">
        查看详情
        <i class="i-lucide-chevron-right" aria-hidden="true"></i>
      </nuxt-link>
    </div>
  </article>
</template>

<script setup lang="ts">
  import type { ArticleItem } from '~/types/site-api'

  import { formatSiteDate, formatVideoDuration, getArticlePath } from '~/utils/content'

  interface Props {
    article: ArticleItem
  }

  defineOptions({
    name: 'ArticleListItem',
  })

  const props = defineProps<Props>()
  const isVideo = computed(() => props.article.media_type === 2)

  const articlePath = computed(() => getArticlePath(props.article))
  const {
    targetRef: revealTarget,
    isReady: isRevealReady,
    isVisible: isRevealed,
  } = useScrollReveal(0.1)

  const publishDate = computed(() => formatSiteDate(props.article.publish_date))
  const videoDuration = computed(() => formatVideoDuration(props.article.video_duration))
</script>

<style scoped lang="scss">
  .article-list-item {
    display: grid;
    grid-template-columns: minmax(180px, 220px) minmax(0, 1fr);
    gap: clamp(18px, 2.4vw, 28px);
    align-items: center;
    min-width: 0;
    padding: 28px 0;
    border-bottom: 1px solid var(--line);
  }

  .article-list-item.is-reveal-ready:not(.is-revealed) {
    opacity: 0;
    transform: translateY(24px);
  }

  .article-list-item.is-reveal-ready.is-revealed {
    opacity: 1;
    transform: none;
    transition:
      opacity 520ms var(--ease),
      transform 520ms var(--ease);
  }

  .article-media-link {
    position: relative;
    display: block;
    width: 100%;
    min-width: 0;
    aspect-ratio: 16 / 10;
    overflow: hidden;
    cursor: pointer;
    background: #eef0f2;
    border: 0;
    border-radius: var(--radius-md);
  }

  .article-media-link :deep(img) {
    transition: transform var(--normal) var(--ease);
  }

  .video-play-mark {
    position: absolute;
    top: 50%;
    left: 50%;
    display: grid;
    place-items: center;
    width: 46px;
    height: 46px;
    color: var(--white);
    background: rgb(35 21 23 / 62%);
    border: 1px solid rgb(255 255 255 / 74%);
    border-radius: 50%;
    transform: translate(-50%, -50%);
    transition:
      background var(--fast) var(--ease),
      transform var(--fast) var(--ease);
  }

  .video-play-mark i {
    width: 18px;
    height: 18px;
    margin-left: 3px;
  }

  .video-duration {
    position: absolute;
    right: 10px;
    bottom: 10px;
    padding: 4px 7px;
    font: 600 12px/1.2 var(--num);
    color: var(--white);
    background: rgb(20 15 16 / 72%);
    border-radius: 4px;
  }

  .article-list-copy {
    min-width: 0;
  }

  .article-list-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 12px;
    align-items: center;
    min-width: 0;
    font-size: 12px;
    color: var(--muted);
  }

  .article-list-meta time {
    flex: 0 0 auto;
    font-family: var(--num);
  }

  .article-list-meta-item {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .article-list-copy h2 {
    margin: 10px 0 0;
    font-size: 22px;
    line-height: 1.38;
  }

  .article-list-copy h2 a {
    display: -webkit-box;
    min-width: 0;
    padding: 0;
    overflow: hidden;
    -webkit-line-clamp: 2;
    font: inherit;
    color: var(--ink);
    text-align: left;
    cursor: pointer;
    background: transparent;
    border: 0;
    transition: color var(--fast) var(--ease);
    -webkit-box-orient: vertical;
  }

  .article-list-copy p {
    display: -webkit-box;
    margin: 10px 0 0;
    overflow: hidden;
    -webkit-line-clamp: 2;
    font-size: 14px;
    line-height: 1.7;
    color: var(--muted);
    -webkit-box-orient: vertical;
  }

  .article-list-action {
    display: inline-flex;
    gap: 4px;
    align-items: center;
    padding: 0;
    margin-top: 12px;
    font-size: 13px;
    font-weight: 400;
    color: var(--more);
    cursor: pointer;
    background: transparent;
    border: 0;
    transition: color var(--fast) var(--ease);
  }

  .article-list-action i {
    width: 14px;
    height: 14px;
    transition: transform var(--fast) var(--ease);
  }

  .article-media-link:focus-visible,
  .article-list-copy h2 a:focus-visible,
  .article-list-action:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 3px;
  }

  @media (hover: hover) and (pointer: fine) {
    .article-list-item:hover .article-media-link :deep(img),
    .article-list-item:focus-within .article-media-link :deep(img) {
      transform: scale(1.035);
    }

    .article-list-item:hover .video-play-mark,
    .article-list-item:focus-within .video-play-mark {
      background: var(--accent);
      transform: translate(-50%, -50%) scale(1.05);
    }

    .article-list-item:hover .article-list-copy h2 a,
    .article-list-item:focus-within .article-list-copy h2 a,
    .article-list-item:hover .article-list-action:not(:disabled),
    .article-list-item:focus-within .article-list-action:not(:disabled) {
      color: var(--accent);
    }

    .article-list-action:hover:not(:disabled) i {
      transform: translateX(3px);
    }
  }

  @media (hover: none) {
    .article-media-link:active :deep(img) {
      transform: scale(1.02);
    }

    .article-media-link:active .video-play-mark {
      background: var(--accent);
      transform: translate(-50%, -50%) scale(1.03);
    }

    .article-list-copy h2 a:active,
    .article-list-action:active:not(:disabled) {
      color: var(--accent);
    }
  }

  @media (min-width: 768px) {
    .article-list-copy {
      display: flex;
      flex-direction: row;
      gap: 48px;
      align-items: center;
    }

    .article-list-text {
      display: flex;
      flex: 1 1 0%;
      flex-direction: column;
      min-width: 0;
    }

    .article-list-text h2 {
      order: 1;
      margin-top: 0;
    }

    .article-list-meta {
      order: 2;
      margin-top: 12px;
      color: var(--more);
    }

    .article-list-text p {
      order: 3;
    }

    .article-list-action {
      flex: 0 0 auto;
      margin-top: 0;
    }
  }

  @media (max-width: 767px) {
    .article-list-item {
      display: flex;
      flex-direction: column;
      gap: 0;
      align-items: stretch;
      padding: 0;
      margin-bottom: 14px;
      overflow: hidden;
      background: var(--surface);
      border: 1px solid var(--line);
      border-radius: var(--radius-md);
      box-shadow: 0 6px 18px rgb(45 31 32 / 6%);
    }

    .article-list-item:last-child {
      margin-bottom: 0;
    }

    .article-media-link {
      aspect-ratio: 16 / 10;
      border-radius: 0;
    }

    .article-media-link :deep(.site-media-fallback span) {
      display: none;
    }

    .article-list-copy {
      display: flex;
      flex-direction: column;
      padding: 14px 16px 16px;
    }

    .article-list-text {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .article-list-copy h2 {
      order: 1;
      margin: 0;
      font-size: 16px;
      line-height: 1.4;
    }

    .article-list-meta {
      flex-wrap: wrap;
      gap: 4px 10px;
      order: 2;
      width: 100%;
      margin: 0;
      font-size: 11px;
      line-height: 1.4;
      color: var(--more);
    }

    .article-list-meta-item {
      flex: 0 1 auto;
    }

    .article-list-copy p {
      display: -webkit-box;
      order: 3;
      margin: 0;
      -webkit-line-clamp: 2;
      font-size: 12px;
      line-height: 1.55;
    }

    .article-list-action {
      display: none;
    }

    .video-play-mark {
      width: 40px;
      height: 40px;
    }

    .video-play-mark i {
      width: 16px;
      height: 16px;
    }

    .video-duration {
      right: 8px;
      bottom: 8px;
      padding: 3px 5px;
      font-size: 10px;
    }
  }

  @media (max-width: 420px) {
    .article-list-copy {
      padding: 12px 12px 14px;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .article-media-link :deep(img),
    .video-play-mark,
    .article-list-action i {
      transition: none;
    }

    .article-list-item:hover .article-media-link :deep(img),
    .article-list-item:focus-within .article-media-link :deep(img),
    .article-media-link:active :deep(img) {
      transform: none;
    }

    .article-list-item:hover .video-play-mark,
    .article-list-item:focus-within .video-play-mark,
    .article-media-link:active .video-play-mark {
      transform: translate(-50%, -50%);
    }

    .article-list-action:hover:not(:disabled) i {
      transform: none;
    }
  }
</style>
